import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const links = [
    { label: "Shop", href: "#products" },
    { label: "Our Story", href: "#about" },
    { label: "Sourcing", href: "#sourcing" },
    { label: "Process", href: "#process" },
    { label: "Bulk / B2B", href: "#bulk" },
    { label: "Contact", href: "#contact" },
];

const Navbar = () => {
    const { totalCount, setOpen } = useCart();
    const [mobile, setMobile] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        if (mobile) {
            const prev = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = prev;
            };
        }
    }, [mobile]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "bg-sun-bg/90 backdrop-blur-xl border-b border-sun-line shadow-sm shadow-sun-ink/5"
                    : "bg-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-20 md:h-24">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
                    <img
                        src="/logo.jpg"
                        alt="Ostrich"
                        className="h-12 md:h-14 w-auto object-contain mix-blend-multiply"
                    />
                    <div className="hidden sm:block leading-tight">
                        <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-sun-orange font-medium">
                            Make your health better
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-sun-muted">
                            Surat · India · est. 2025
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className="font-body text-sm font-medium text-sun-ink hover:text-sun-orange transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setOpen(true)}
                        data-testid="open-cart-button"
                        className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-sun-ink text-sun-bg hover:bg-sun-orange transition-colors"
                        aria-label="Open cart"
                    >
                        <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                        {totalCount > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-1 -right-1 bg-sun-gold text-sun-ink font-mono text-[10px] font-semibold w-5 h-5 flex items-center justify-center rounded-full ring-2 ring-sun-bg"
                            >
                                {totalCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setMobile(true)}
                        className="lg:hidden inline-flex items-center justify-center w-12 h-12 rounded-full border border-sun-ink/20 text-sun-ink"
                        aria-label="Open menu"
                        data-testid="open-mobile-menu"
                    >
                        <Menu className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {mobile && (
                <div
                    data-testid="mobile-menu-overlay"
                    className="lg:hidden fixed inset-0 z-[100] flex flex-col"
                    style={{ backgroundColor: "#FFF8F1" }}
                >
                    <div className="flex justify-between items-center h-20 px-6 border-b border-sun-line">
                        <img src="/logo.jpg" alt="Ostrich" className="h-10 w-auto mix-blend-multiply" />
                        <button
                            onClick={() => setMobile(false)}
                            data-testid="close-mobile-menu"
                            className="w-12 h-12 flex items-center justify-center rounded-full border border-sun-ink/20"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <nav className="flex-1 flex flex-col justify-center px-6 gap-2 overflow-y-auto" style={{ backgroundColor: "#FFF8F1" }}>
                        {links.map((l, i) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setMobile(false)}
                                className="font-heading text-5xl border-b border-sun-line py-5 text-sun-ink hover:text-sun-orange transition-colors flex items-baseline gap-4"
                            >
                                <span className="font-mono text-xs text-sun-muted">0{i + 1}</span>
                                <span className="display-italic">{l.label}</span>
                            </a>
                        ))}
                    </nav>
                    <div className="p-6 border-t border-sun-line space-y-1" style={{ backgroundColor: "#FFF8F1" }}>
                        <a
                            href="tel:+919737028999"
                            className="block font-mono text-[10px] uppercase tracking-[0.28em] text-sun-muted hover:text-sun-orange"
                        >
                            +91 9737 028 999
                        </a>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-sun-muted">
                            Mon–Sat 10:00–18:00 IST
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
